/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { ICustomer } from "../../../../interfaces/customers";
import { ECustomerSupplierLinkResult, ICustomerSupplierConflict } from "../../../../interfaces/customersSuppliers";
import {
  copyCustomerSupplierConflictData,
  getCustomerSupplierConflict,
  hasCustomerSupplierChanges,
} from "@/utils/customerSupplierSync";
import { useParams } from "react-router-dom";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { handleUpload } from "../../../../utils/handleUpload";
import { createCustomer, createFile, listCustomers, showCustomer, updateCustomer, deleteCustomer } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const defaultCustomer: ICustomer = {
  name: "",
  nameFantasy: "",
  description: "",
  active: true,
  document: "",
  email: "",
  address: "",
  complement: "",
  createdIn: undefined,
};

export const useCustomers = ({
  disabledShowId,
  disabledGetAll,
  filter: filterInitial,
}: {
  filter?: Record<string, any>;
  disabledShowId?: boolean;
  disabledGetAll?: boolean;
}) => {
  const { idCustomer: id } = useParams();
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [customer, setCustomer] = useState<ICustomer>(defaultCustomer);
  const [savedCustomer, setSavedCustomer] = useState<ICustomer>(defaultCustomer);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState<boolean>(false);
  const [customerSupplierResult, setCustomerSupplierResult] = useState<ECustomerSupplierLinkResult>();
  const [customerSupplierConflict, setCustomerSupplierConflict] = useState<ICustomerSupplierConflict>();
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit: 25 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/customers" });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCustomer = (key: string, value: any) => {
    setCustomer((curr) => {
      const newData = updateValue(curr, key, value);
      return { ...curr, ...newData };
    });
  };

  const uploadImagem = (
    fileUpload: any,
    onProgress?: (data: number) => void
  ) => {
    setLoading(true);
    createFile({ file: fileUpload })
      .then(({ file: fileCreate }) => {
        handleUpload({
          dataUpload: { urlUpload: fileCreate.urlUpload, ...fileUpload },
          onProgress,
        }).then(async () => {
          await updateCustomer({
            id: customer.id!,
            customer: { idFilePhoto: fileCreate.id },
          });
        });
      })
      .finally(() => setLoading(false));
  };

  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter,
        sort,
      }: {
        page?: number;
        limit?: number;
        filter?: any;
        sort?: any;
      }) => {
        setLoading(true);
        listCustomers({
          includes: ["createdBy", "updatedBy"],
          page,
          filter: filter,
          limit,
          sort,
        })
          .then(
            ({ customers: listCustomers, limit, currentPage, totalItems }) => {
              setCustomers(listCustomers);
              
              setLimit(Number(limit));
              setTotalItems(Number(totalItems));
            }
          )
          .finally(() => setLoading(false));
      },
      150
    ),
    []
  );

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showCustomer({ id })
      .then(({ customer: customersShow }) => {
        setCustomer(customersShow);
        setSavedCustomer(customersShow);
      })
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({ list: listCustomers, key: "customers", filter });

  /** Abre o modal de conflito quando o documento já pertence a outro cadastro com dados diferentes. */
  const handleSaveError = (error: unknown) => {
    const conflict = getCustomerSupplierConflict(error);
    if (conflict) setCustomerSupplierConflict(conflict);
  };

  const createWith = (data: ICustomer) => {
    setLoading(true);
    createCustomer({ customer: data })
      .then(({ customer: customerCreate, customerSupplierResult: result }) => {
        toast.success(`Cliente (${customerCreate.name}) criado com sucesso!`);
        setCustomerSupplierConflict(undefined);
        setCustomerSupplierResult(result);
        goEdit(customerCreate.id!);
      })
      .catch(handleSaveError)
      .finally(() => setLoading(false));
  };

  const updateWith = (data: ICustomer) => {
    setLoading(true);
    updateCustomer({ id: data.id!, customer: data })
      .then(({ customer: customerUpdate, customerSupplierResult: result }) => {
        toast.success(
          `Cliente (${customerUpdate.name}) atualizado com sucesso!`
        );
        setSavedCustomer(customerUpdate);
        setCustomerSupplierConflict(undefined);
        if (result !== ECustomerSupplierLinkResult.AlreadyExisted) setCustomerSupplierResult(result);
        goEdit(customerUpdate.id!);
      })
      .catch(handleSaveError)
      .finally(() => setLoading(false));
  };

  const create = () => createWith(customer);

  const update = () => updateWith(customer);

  /** Copia para o formulário os dados do cadastro que já usa o documento e salva com eles. */
  const copyConflictAndSave = () => {
    if (!customerSupplierConflict) return;
    const data = copyCustomerSupplierConflictData(customer, customerSupplierConflict);
    setCustomer(data);
    if (data.id) updateWith(data);
    else createWith(data);
  };

  /** Pede confirmação antes de salvar quando a edição altera também o cliente/fornecedor do Financeiro. */
  const requestUpdate = () => {
    if (hasCustomerSupplierChanges(savedCustomer, customer)) {
      setConfirmSyncOpen(true);
      return;
    }
    update();
  };

  const confirmSync = () => {
    setConfirmSyncOpen(false);
    update();
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter, sort });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteCustomer({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Cliente",
        plural: "clientes",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    customers,
    customer,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleCustomer,
    create,
    id,
    update,
    requestUpdate,
    confirmSyncOpen,
    confirmSync,
    cancelSync: () => setConfirmSyncOpen(false),
    customerSupplierResult,
    closeCustomerSupplierResult: () => setCustomerSupplierResult(undefined),
    customerSupplierConflict,
    closeCustomerSupplierConflict: () => setCustomerSupplierConflict(undefined),
    copyConflictAndSave,
    setTotalItems,
    totalItems,
    handleFilter,
    handleSort,
    search,
    uploadImagem,
  };
};
